"use client";

import React, { useEffect, useMemo, useState } from "react";
import Modal from "Common/Components/Modal";
import Select from "react-select";
import { Plus, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppServices } from "hook/services";
import { useParams } from "react-router-dom";

type Folder = { _id: string; name: string; type?: string };

interface Props {
  folders: Folder[]; // parent folders list
  handleRefresh: () => void;
}

type OptionItem = { key: string; label: string; url?: string };
  // React Select custom styles
  const customSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      borderColor: state.isFocused 
        ? '#3abab4' 
        : '#e2e8f0',
      borderRadius: '0.5rem',
      padding: '2px 8px',
      backgroundColor: 'white',
      minHeight: '44px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(58, 186, 180, 0.2)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#3abab4' : '#94a3b8'
      }
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: '0.5rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#3abab4' 
        : state.isFocused 
          ? '#f0fdfa' 
          : 'white',
      color: state.isSelected ? 'white' : '#334155',
      '&:active': {
        backgroundColor: '#3abab4'
      }
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: '#f0fdfa',
      borderRadius: '0.375rem',
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: '#0f766e',
      fontWeight: '500',
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: '#0f766e',
      '&:hover': {
        backgroundColor: '#fda4af',
        color: '#dc2626',
      },
    }),
  };
const groupedFieldTypes = [
  {
    groupLabel: "Text Input",
    types: [
      { value: "single_line", label: "Single Line" },
      { value: "multi_line", label: "Multi Line" },
      { value: "text_box_list", label: "Text Box List" },
    ],
  },
  {
    groupLabel: "Values",
    types: [
      { value: "number", label: "Number" },
      { value: "phone", label: "Phone" },
      { value: "monetary", label: "Monetary" },
    ],
  },
  {
    groupLabel: "Choosing Options",
    types: [
      { value: "dropdown_single", label: "Dropdown (Single)" },
      { value: "dropdown_multiple", label: "Dropdown (Multiple)" },
      { value: "radio_select", label: "Radio Select" },
      { value: "checkbox", label: "Checkbox" },
      { value: "date_picker", label: "Date Picker" },
      { value: "file_upload", label: "File Upload" },
    ],
  },
];

const allDataTypeOptions = groupedFieldTypes.flatMap((g) => g.types);

// Object options for filtering folders
const objectOptions = [
  { value: "contact", label: "Contact" },
  { value: "company", label: "Company" },
  { value: "opportunity", label: "Opportunity" },
];

// File format options
const fileFormatOptions = [
  { value: ".pdf", label: "PDF (.pdf)" },
  { value: ".jpg,.jpeg", label: "JPEG Images (.jpg, .jpeg)" },
  { value: ".png", label: "PNG Images (.png)" },
  { value: ".doc,.docx", label: "Word Documents (.doc, .docx)" },
  { value: ".xls,.xlsx", label: "Excel Files (.xls, .xlsx)" },
  { value: ".txt", label: "Text Files (.txt)" },
  { value: ".zip,.rar", label: "Archive Files (.zip, .rar)" },
];

const FieldCreateModal: React.FC<Props> = ({ folders, handleRefresh }) => {
  const AppService = useAppServices();
  const { locationId } = useParams(); // fallback location
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedObject, setSelectedObject] = useState<string>("");

  const toggle = () => {
    setOpen((v) => !v);
    if (!open) {
      // resetting step to 1 when opening
      setStep(1);
      setSelectedObject("");
      formik.resetForm();
    }
  };

  // Filter folders based on selected object
  const filteredFolders = useMemo(() => {
    if (!selectedObject) return folders;
    return folders.filter(folder => folder.type === selectedObject);
  }, [folders, selectedObject]);

  // Formik initial / validation
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      description: "",
      placeholder: "",
      dataType: "",
      fieldKey: "",
      acceptedFormats: [] as string[],
      maxFileLimit: 1 as number | "",
      parentId: "",
      options: [] as OptionItem[],
      locationId: locationId || "",
      object: "",
    },
    validationSchema: Yup.object().shape({
      // Step 2 validation (we'll manually control when to validate)
      name: Yup.string().when(["dataType", "parentId"], {
        is: () => true,
        then: (s) => s.trim().min(1).required("Name is required"),
      }),
      dataType: Yup.string().required("Please select a field type"),
      parentId: Yup.string().required("Please select a folder"),
      fieldKey: Yup.string().required(),
      acceptedFormats: Yup.array().when("dataType", {
        is: "file_upload",
        then: (s) => s.min(1, "At least one format is required"),
        otherwise: (s) => s.optional(),
      }),
      maxFileLimit: Yup.number().when("dataType", {
        is: "file_upload",
        then: (s) =>
          s.required("Max file limit required").min(1, "Must be >= 1"),
        otherwise: (s) => s.optional(),
      }),
      object: Yup.string().required("Please select an object"),
      options: Yup.array().when("dataType", {
        is: (val: string) =>
          val === "dropdown_single" || val === "dropdown_multiple" || val === "radio_select" || val === "checkbox",
        then: (schema) =>
          schema.of(
            Yup.object({
              key: Yup.string().required("Key required"),
              label: Yup.string().required("Label required"),
              url: Yup.string().optional().nullable(),
            })
          ).min(1, "Provide at least one option"),
        otherwise: (s) => s.optional(),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          name: values.name.trim(),
          fieldKey:
            values.fieldKey ||
            `${values.object}.${values.name.trim().replace(/\s+/g, "_").toLowerCase()}`,
          description: values.description?.trim() || "",
          placeholder: values.placeholder?.trim() || "",
          options:
            values.options && values.options.length ? values.options : undefined,
          acceptedFormats: values.acceptedFormats?.length > 0 ? values.acceptedFormats : undefined,
          dataType: values.dataType,
          maxFileLimit:
            values.dataType === "file_upload" ? Number(values.maxFileLimit) : undefined,
          parentId: values.parentId || undefined,
          locationId: values.locationId || locationId || undefined,
          object: values.object || undefined,
        };

        const { response } = await AppService.custom_field.field.create({
          payload,
          toaster: true,
        });

        if (response) {
          formik.resetForm();
          setStep(1);
          setSelectedObject("");
          setOpen(false);
          handleRefresh();
        }
      } catch (err) {
        console.error(err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Auto generate fieldKey from name (hidden)
  useEffect(() => {
    if (formik.values.name && !formik.touched.fieldKey) {
      const slug = formik.values.name
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^\w\-_.]/g, "")
        .toLowerCase();
      formik.setFieldValue("fieldKey", `${formik?.values.object}.${slug}`, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.name]);

  // Reset folders when object changes
  useEffect(() => {
    if (selectedObject) {
      formik.setFieldValue("object", selectedObject);
      formik.setFieldValue("parentId", ""); // Reset folder selection
    }
  }, [selectedObject]);

  // helpers for options
  const addOption = () => {
    const arr = [...formik.values.options];
    arr.push({ key: "", label: "", url: "" });
    formik.setFieldValue("options", arr);
  };
  const removeOption = (index: number) => {
    const arr = [...formik.values.options];
    arr.splice(index, 1);
    formik.setFieldValue("options", arr);
  };

// Preview renderer - Using React Select for dropdowns
const Preview = useMemo(() => {
  const dt = formik.values.dataType;
  const placeholder = formik.values.placeholder || "";
  

  switch (dt) {
    case "single_line":
      return (
        <input
          className="w-full border border-slate-200 dark:border-zink-500 rounded-lg px-3 py-2 bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500"
          placeholder={placeholder || "Single line text"}
        />
      );
    case "multi_line":
      return (
        <textarea
          className="w-full border border-slate-200 dark:border-zink-500 rounded-lg px-3 py-2 bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500"
          placeholder={placeholder || "Multi line text"}
          rows={3}
        />
      );
    case "text_box_list":
      return (
        <div className="space-y-2">
          <input className="w-full border border-slate-200 dark:border-zink-500 rounded-lg px-3 py-2 bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500" placeholder="Item 1" />
          <input className="w-full border border-slate-200 dark:border-zink-500 rounded-lg px-3 py-2 bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500" placeholder="Item 2" />
        </div>
      );
    case "number":
      return <input type="number" className="w-full border border-slate-200 dark:border-zink-500 rounded-lg px-3 py-2 bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500" placeholder="Number" />;
    case "phone":
      return <input type="tel" className="w-full border border-slate-200 dark:border-zink-500 rounded-lg px-3 py-2 bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500" placeholder="Phone" />;
    case "monetary":
      return <input type="number" className="w-full border border-slate-200 dark:border-zink-500 rounded-lg px-3 py-2 bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500" placeholder="0.00" />;
    case "dropdown_single": {
      const opts = formik.values.options || [];
      const options = opts.map((opt, index) => ({
        value: opt.key || `option-${index}`,
        label: opt.label || `Option ${index + 1}`
      }));
      
      return (
        <Select
          placeholder="Select an option..."
          options={options}
          styles={customSelectStyles}
          className="react-select-container"
          classNamePrefix="react-select"
          isSearchable
        />
      );
    }
    case "dropdown_multiple": {
      const opts = formik.values.options || [];
      const options = opts.map((opt, index) => ({
        value: opt.key || `option-${index}`,
        label: opt.label || `Option ${index + 1}`
      }));
      
      return (
        <Select
          placeholder="Select options..."
          options={options}
          isMulti
          styles={customSelectStyles}
          className="react-select-container"
          classNamePrefix="react-select"
          isSearchable
        />
      );
    }
    case "radio_select": {
      const opts = formik.values.options || [];
      return (
        <div className="space-y-2">
          {opts.slice(0, 3).map((o, i) => (
            <label key={i} className="flex items-center gap-2 text-slate-600 dark:text-zink-200 cursor-pointer">
              <input type="radio" name="preview-radio" className="text-custom-500 focus:ring-custom-500" />
              <span>{o.label || `Option ${i + 1}`}</span>
            </label>
          ))}
        </div>
      );
    }
    case "checkbox": {
      const opts = formik.values.options || [];
      return (
        <div className="space-y-2">
          {opts.slice(0, 3).map((o, i) => (
            <label key={i} className="flex items-center gap-2 text-slate-600 dark:text-zink-200 cursor-pointer">
              <input type="checkbox" className="rounded text-custom-500 focus:ring-custom-500" />
              <span>{o.label || `Option ${i + 1}`}</span>
            </label>
          ))}
        </div>
      );
    }
    case "date_picker":
      return <input type="date" className="w-full border border-slate-200 dark:border-zink-500 rounded-lg px-3 py-2 bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500" />;
    case "file_upload":
      return (
        <div className="w-full border-2 border-dashed border-slate-200 dark:border-zink-500 rounded-lg px-3 py-4 bg-white dark:bg-zink-700 flex flex-col items-center justify-center cursor-pointer hover:border-custom-500 dark:hover:border-custom-500 transition-colors">
          <div className="text-slate-600 dark:text-zink-200 text-sm mb-1">Click to upload or drag and drop</div>
          <div className="text-xs text-slate-400 dark:text-zink-400">
            {formik.values.acceptedFormats?.join(", ") || "Select file formats"}
          </div>
        </div>
      );
    default:
      return <div className="text-slate-400 dark:text-zink-400 text-center py-8">Pick a field type to preview</div>;
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [formik.values.dataType, formik.values.options, formik.values.placeholder, formik.values.acceptedFormats]);

  // Step 1: choose type UI card
  const TypeCard = ({ typeValue, typeLabel, active, onClick }: { typeValue: string; typeLabel: string; active: boolean; onClick: () => void }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left border rounded-lg p-4 transition-all duration-200 ${
        active 
          ? "bg-custom-50 dark:bg-custom-500/20 border-custom-500 shadow-sm" 
          : "bg-white dark:bg-zink-700 border-slate-200 dark:border-zink-500 hover:border-custom-300 dark:hover:border-custom-400 hover:shadow-md"
      }`}
    >
      <div className={`font-medium text-sm ${
        active ? "text-custom-600 dark:text-custom-400" : "text-slate-700 dark:text-zink-200"
      }`}>
        {typeLabel}
      </div>
    </button>
  );

  // move to step 2 only if a dataType selected
  const handleNext = () => {
    if (!formik.values.dataType) return;
    setStep(2);
  };



  return (
    <>
      <button 
        onClick={toggle} 
        className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
      >
        <Plus className="inline-block size-4 mr-1" />
        <span>Add Field</span>
      </button>

      <Modal
        show={open}
        onHide={() => {
          setOpen(false);
          setStep(1);
          setSelectedObject("");
          formik.resetForm();
        }}
        id="customFieldModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[65rem] bg-white dark:bg-zink-700 shadow-xl rounded-lg"
      >
        <Modal.Header className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-zink-500">
          <Modal.Title className="text-xl font-semibold text-slate-800 dark:text-zink-100">
            Create Custom Field
          </Modal.Title>
          <div className="flex items-center gap-2">
            <div className="px-3 py-1 bg-slate-100 dark:bg-zink-600 rounded-full text-sm font-medium text-slate-600 dark:text-zink-300">
              Step {step} of 2
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-6 max-h-[70vh] overflow-y-auto">
          {step === 1 ? (
            <div className="grid grid-cols-12 gap-8">
              {/* Left: type grid */}
              <div className="col-span-7">
                {groupedFieldTypes.map((group) => (
                  <div key={group.groupLabel} className="mb-8">
                    <h5 className="text-base font-semibold text-slate-800 dark:text-zink-100 mb-4 pb-2 border-b border-slate-100 dark:border-zink-500">
                      {group.groupLabel}
                    </h5>
                    <div className="grid grid-cols-2 gap-4">
                      {group.types.map((t) => (
                        <TypeCard
                          key={t.value}
                          typeValue={t.value}
                          typeLabel={t.label}
                          active={formik.values.dataType === t.value}
                          onClick={() => formik.setFieldValue("dataType", t.value)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Right: preview */}
              <div className="col-span-5">
                <div className="sticky top-0">
                  <div className="mb-4">
                    <h5 className="text-base font-semibold text-slate-800 dark:text-zink-100 mb-3">Preview</h5>
                    <div className="border border-slate-200 dark:border-zink-500 rounded-lg p-6 bg-slate-50 dark:bg-zink-600 min-h-[200px] flex items-center justify-center">
                      {Preview}
                    </div>
                  </div>

                  <div className="flex justify-between gap-3 pt-4 border-t border-slate-200 dark:border-zink-500">
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        setStep(1);
                        setSelectedObject("");
                        formik.resetForm();
                      }}
                      className="px-6 py-2.5 border border-slate-200 dark:border-zink-500 rounded-lg text-slate-600 dark:text-zink-300 hover:bg-slate-50 dark:hover:bg-zink-600 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`px-6 py-2.5 rounded-lg text-white font-medium transition-all duration-200 ${
                        formik.values.dataType 
                          ? "bg-custom-500 hover:bg-custom-600 shadow-sm hover:shadow-md" 
                          : "bg-slate-300 dark:bg-zink-500 cursor-not-allowed"
                      }`}
                      onClick={handleNext}
                      disabled={!formik.values.dataType}
                    >
                      Continue
                      <ChevronRight className="inline-block size-4 ml-1" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Step 2: details form
            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-8">
                <form onSubmit={formik.handleSubmit} className="space-y-6">
                  {/* Object & Name Row */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-800 dark:text-zink-100 mb-2">
                        Object <span className="text-red-500">*</span>
                      </label>
                      <Select
                        placeholder="Select object..."
                        options={objectOptions}
                        value={objectOptions.find((o) => o.value === selectedObject) || null}
                        onChange={(opt: any) => setSelectedObject(opt?.value || "")}
                        styles={customSelectStyles}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                      {formik.touched.object && formik.errors.object && (
                        <p className="text-red-500 text-sm mt-1">{String(formik.errors.object)}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-800 dark:text-zink-100 mb-2">
                        Field Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full px-3 py-2.5 border rounded-lg bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 placeholder-slate-400 dark:placeholder-zink-300 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500 ${
                          formik.touched.name && formik.errors.name ? "border-red-500" : "border-slate-200 dark:border-zink-500"
                        }`}
                        placeholder="e.g. City, Department, Status"
                      />
                      {formik.touched.name && formik.errors.name && (
                        <p className="text-red-500 text-sm mt-1">{String(formik.errors.name)}</p>
                      )}
                    </div>
                  </div>

                  {/* Folder */}
                  <div>
                    <label className="block text-sm font-medium text-slate-800 dark:text-zink-100 mb-2">
                      Folder <span className="text-red-500">*</span>
                    </label>
                    <Select
                      placeholder={selectedObject ? "Select folder..." : "Please select an object first"}
                      options={filteredFolders.map((f) => ({ value: f._id, label: f.name }))}
                      value={filteredFolders.map((f) => ({ value: f._id, label: f.name })).find((o) => o.value === formik.values.parentId) || null}
                      onChange={(opt: any) => formik.setFieldValue("parentId", opt?.value || "")}
                      isDisabled={!selectedObject}
                      styles={customSelectStyles}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                    {formik.touched.parentId && formik.errors.parentId && (
                      <p className="text-red-500 text-sm mt-1">{String(formik.errors.parentId)}</p>
                    )}
                    {!selectedObject && (
                      <p className="text-amber-500 text-sm mt-1">Please select an object to see available folders</p>
                    )}
                  </div>

                  {/* Description & Placeholder Row */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-800 dark:text-zink-100 mb-2">
                        Description
                      </label>
                      <input
                        name="description"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        className="w-full px-3 py-2.5 border border-slate-200 dark:border-zink-500 rounded-lg bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 placeholder-slate-400 dark:placeholder-zink-300 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500"
                        placeholder="Field description (optional)"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-800 dark:text-zink-100 mb-2">
                        Placeholder
                      </label>
                      <input
                        name="placeholder"
                        value={formik.values.placeholder}
                        onChange={formik.handleChange}
                        className="w-full px-3 py-2.5 border border-slate-200 dark:border-zink-500 rounded-lg bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 placeholder-slate-400 dark:placeholder-zink-300 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500"
                        placeholder="Placeholder text (optional)"
                      />
                    </div>
                  </div>

                  {/* Field Key (Read-only) */}
                  <div>
                    <label className="block text-sm font-medium text-slate-800 dark:text-zink-100 mb-2">
                      Field Key
                    </label>
                    <input 
                      name="fieldKey" 
                      value={formik.values.fieldKey || ""} 
                      readOnly 
                      className="w-full px-3 py-2.5 border border-slate-200 dark:border-zink-500 rounded-lg bg-slate-50 dark:bg-zink-600 text-slate-500 dark:text-zink-300 cursor-not-allowed"
                    />
                    <p className="text-xs text-slate-400 dark:text-zink-400 mt-1">
                      Auto-generated field identifier
                    </p>
                  </div>

                  {/* Conditional: options for select/multi/radio/checkbox */}
                  {(formik.values.dataType === "dropdown_single" ||
                    formik.values.dataType === "dropdown_multiple" ||
                    formik.values.dataType === "radio_select" ||
                    formik.values.dataType === "checkbox") && (
                    <div className="border border-slate-200 dark:border-zink-500 rounded-lg p-4 bg-slate-50 dark:bg-zink-600/30">
                      <label className="block text-sm font-medium text-slate-800 dark:text-zink-100 mb-3">
                        Options <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-3">
                        {formik.values.options.map((opt, idx) => (
                          <div key={idx} className="grid grid-cols-12 gap-3 items-center">
                            <div className="col-span-5">
                              <input
                                className="w-full px-3 py-2 border border-slate-200 dark:border-zink-500 rounded bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 placeholder-slate-400 dark:placeholder-zink-300 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500"
                                placeholder="Key (unique identifier)"
                                value={opt.key}
                                onChange={(e) => formik.setFieldValue(`options[${idx}].key`, e.target.value)}
                              />
                            </div>
                            <div className="col-span-5">
                              <input
                                className="w-full px-3 py-2 border border-slate-200 dark:border-zink-500 rounded bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 placeholder-slate-400 dark:placeholder-zink-300 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500"
                                placeholder="Display label"
                                value={opt.label}
                                onChange={(e) => formik.setFieldValue(`options[${idx}].label`, e.target.value)}
                              />
                            </div>
                            <div className="col-span-2 flex justify-center">
                              {formik.values.options.length > 1 && (
                                <button 
                                  type="button" 
                                  onClick={() => removeOption(idx)}
                                  className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
                                >
                                  <Trash2 className="size-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <button 
                        type="button" 
                        onClick={addOption}
                        className="mt-3 text-custom-500 hover:text-custom-600 font-medium text-sm flex items-center gap-1 transition-colors"
                      >
                        <Plus className="size-4" />
                        Add Option
                      </button>
                      {formik.touched.options && typeof formik.errors.options === "string" && (
                        <p className="text-red-500 text-sm mt-2">{formik.errors.options as string}</p>
                      )}
                    </div>
                  )}

                  {/* Conditional: file fields */}
                  {formik.values.dataType === "file_upload" && (
                    <div className="border border-slate-200 dark:border-zink-500 rounded-lg p-4 bg-slate-50 dark:bg-zink-600/30">
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-800 dark:text-zink-100 mb-2">
                            Accepted Formats <span className="text-red-500">*</span>
                          </label>
                          <Select
                            placeholder="Select file formats..."
                            options={fileFormatOptions}
                            value={fileFormatOptions.filter(opt => 
                              formik.values.acceptedFormats?.includes(opt.value)
                            )}
                            onChange={(selectedOptions: any) => {
                              const formats = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
                              formik.setFieldValue("acceptedFormats", formats);
                            }}
                            isMulti
                            styles={customSelectStyles}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                          {formik.touched.acceptedFormats && formik.errors.acceptedFormats && (
                            <p className="text-red-500 text-sm mt-1">{String(formik.errors.acceptedFormats)}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-800 dark:text-zink-100 mb-2">
                            Max File Limit <span className="text-red-500">*</span>
                          </label>
                          <input 
                            name="maxFileLimit" 
                            type="number" 
                            min={1} 
                            value={formik.values.maxFileLimit as any} 
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={`w-full px-3 py-2.5 border rounded-lg bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 placeholder-slate-400 dark:placeholder-zink-300 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500 ${
                              formik.touched.maxFileLimit && formik.errors.maxFileLimit ? "border-red-500" : "border-slate-200 dark:border-zink-500"
                            }`}
                          />
                          {formik.touched.maxFileLimit && formik.errors.maxFileLimit && (
                            <p className="text-red-500 text-sm mt-1">{String(formik.errors.maxFileLimit)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="flex justify-between gap-3 pt-6 border-t border-slate-200 dark:border-zink-500">
                    <button 
                      type="button" 
                      onClick={() => { setStep(1); }} 
                      className="px-6 py-2.5 border border-slate-200 dark:border-zink-500 rounded-lg text-slate-600 dark:text-zink-300 hover:bg-slate-50 dark:hover:bg-zink-600 transition-colors font-medium flex items-center gap-2"
                    >
                      <ChevronLeft className="size-4" />
                      Back
                    </button>
                    <div className="flex gap-3">
                      <button 
                        type="button"
                        onClick={() => {
                          setOpen(false);
                          setStep(1);
                          setSelectedObject("");
                          formik.resetForm();
                        }}
                        className="px-6 py-2.5 border border-slate-200 dark:border-zink-500 rounded-lg text-slate-600 dark:text-zink-300 hover:bg-slate-50 dark:hover:bg-zink-600 transition-colors font-medium"
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="px-6 py-2.5 rounded-lg bg-custom-500 hover:bg-custom-600 text-white font-medium shadow-sm hover:shadow-md transition-all duration-200"
                      >
                        Create Field
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              {/* Right: preview + summary */}
              <div className="col-span-4">
                <div className="sticky top-0 space-y-6">
                  <div>
                    <h5 className="text-base font-semibold text-slate-800 dark:text-zink-100 mb-3">Live Preview</h5>
                    <div className="border border-slate-200 dark:border-zink-500 rounded-lg p-6 bg-white dark:bg-zink-700">
                      {Preview}
                    </div>
                  </div>

                  <div className="border border-slate-200 dark:border-zink-500 rounded-lg p-4 bg-slate-50 dark:bg-zink-600/30">
                    <h6 className="font-semibold text-slate-800 dark:text-zink-100 mb-3 text-sm uppercase tracking-wide">Field Summary</h6>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-zink-400">Object:</span>
                        <span className="font-medium text-slate-700 dark:text-zink-200">
                          {objectOptions.find(o => o.value === selectedObject)?.label || "-"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-zink-400">Type:</span>
                        <span className="font-medium text-slate-700 dark:text-zink-200">
                          {allDataTypeOptions.find(t => t.value === formik.values.dataType)?.label || "-"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-zink-400">Key:</span>
                        <span className="font-medium text-slate-700 dark:text-zink-200 text-xs font-mono">
                          {formik.values.fieldKey || "-"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 dark:text-zink-400">Folder:</span>
                        <span className="font-medium text-slate-700 dark:text-zink-200">
                          {folders.find(f => f._id === formik.values.parentId)?.name || "-"}
                        </span>
                      </div>
                      {formik.values.dataType === "file_upload" && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-zink-400">Formats:</span>
                            <span className="font-medium text-slate-700 dark:text-zink-200">
                              {formik.values.acceptedFormats?.join(", ") || "-"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-zink-400">Max files:</span>
                            <span className="font-medium text-slate-700 dark:text-zink-200">
                              {formik.values.maxFileLimit || "-"}
                            </span>
                          </div>
                        </>
                      )}
                      {(formik.values.dataType === "dropdown_single" || formik.values.dataType === "dropdown_multiple" || formik.values.dataType === "radio_select" || formik.values.dataType === "checkbox") && (
                        <div>
                          <div className="flex justify-between mb-2">
                            <span className="text-slate-500 dark:text-zink-400">Options:</span>
                            <span className="font-medium text-slate-700 dark:text-zink-200">
                              {formik.values.options.filter(o => o.label || o.key).length}
                            </span>
                          </div>
                          <ul className="space-y-1 text-xs">
                            {formik.values.options.slice(0, 3).map((o, i) => (
                              <li key={i} className="text-slate-600 dark:text-zink-300 truncate">
                                {o.label || o.key || `Option ${i+1}`}
                              </li>
                            ))}
                            {formik.values.options.length > 3 && (
                              <li className="text-slate-400 dark:text-zink-400 text-xs">
                                +{formik.values.options.length - 3} more
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default FieldCreateModal;